import {Component, forwardRef, Input} from '@angular/core';
import {FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR} from '@angular/forms';
import {WidgetValidator} from '@pt/pt-models';
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {WidgetUtils} from '../../widgets/widget.utils';
import {ValidatorGenerator} from '../../utils/validator-generator';

@Component({
  selector: 'pt-input-radio',
  templateUrl: './input-radio.component.html',
  styles: [`
    .form-check {
      display: flex;
      align-items: center;
    }

    .form-check input {
      margin-top: 0;
    }

    .form-check--disabled {
      pointer-events: none !important;
    }

    .form-check-input--disabled {
      opacity: 50%;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputRadioComponent)
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputRadioComponent),
      multi: true
    }
  ]
})
export class InputRadioComponent /*implements OnInit, OnDestroy, ControlValueAccessor*/ {
  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() help: string;
  @Input() options: InputRadioData[];

  public formGroup: FormGroup;
  isRequired: boolean;
  inputId: string;
  isChanged = false;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {

    this.inputId = WidgetUtils.generateId('ptWCheckbox_' + this.controlName + '_');

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);

    this.setFormGroup();

    this.formGroup.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(t => {
      if (this.isEnabledRadio(t.value)) {
        this.isChanged = t.value !== null;
        if (this.onChanged) {
          this.onChanged(t.value);
        }
        if (this.onTouched) {
          this.onTouched();
        }
      }
    });
  }

  writeValue(obj: any): void {
    this.formGroup.patchValue({value: obj});
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.controls.value.disable();
    } else {
      this.formGroup.controls.value.enable();
    }
  }

  validate(value: FormControl) {
    return this.formGroup.invalid && {
      invalid: true
    };
  }

  private setFormGroup() {
    const validator = ValidatorGenerator.generateValidator(this.validators);
    this.formGroup = new FormGroup({
      value: new FormControl(null, validator)
    });
  }

  private isEnabledRadio(radioValue: any) {
    const option = this.options.find(option => option.value === radioValue);
    return !option?.disabled;
  }
}

export interface InputRadioData {
  label: Observable<string>,
  value: any,
  disabled?: boolean,
}
